import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { EhrReprescribingPrefill, EhrRecentMedicationRequestsResult } from './ehrLaunchPatientApi'
import {
  ehrLaunchEstablished,
  ehrPatientAcquisitionCleared,
  ehrPatientInvalidated,
} from './ehrPatientAcquisitionActions'
import { resetScript } from '../../pages/NewScriptPage/ScriptForm/PrescribedItem/PrescribedItemSlice'

export type EhrRecentMedicationRequestsPhase = 'idle' | 'loading' | 'ready' | 'dismissed'

export interface EhrRecentMedicationRequestsState {
  launchInstanceId?: string
  phase: EhrRecentMedicationRequestsPhase
  result?: EhrRecentMedicationRequestsResult
  represcribing?: {
    phase: 'matching' | 'error'
    rowIndex: number
    attemptId: string
    message?: string
  }
  pendingSeed?: {
    launchInstanceId: string
    slotIndex: number
    prefill: EhrReprescribingPrefill
    selectionAttemptId?: string
  }
}

const initialState: EhrRecentMedicationRequestsState = { phase: 'idle' }

const slice = createSlice({
  name: 'ehrRecentMedicationRequests',
  initialState,
  reducers: {
    ehrRecentMedicationRequestsStarted: (
      state,
      { payload }: PayloadAction<{ launchInstanceId: string }>
    ) => {
      if (
        state.launchInstanceId === payload.launchInstanceId &&
        (state.phase === 'loading' || state.phase === 'ready' || state.phase === 'dismissed')
      )
        return
      state.launchInstanceId = payload.launchInstanceId
      state.phase = 'loading'
      state.result = undefined
    },
    ehrRecentMedicationRequestsReady: (
      state,
      {
        payload,
      }: PayloadAction<{
        launchInstanceId: string
        result: EhrRecentMedicationRequestsResult
      }>
    ) => {
      if (state.launchInstanceId !== payload.launchInstanceId || state.phase !== 'loading') return
      state.phase = 'ready'
      state.result = payload.result
    },
    dismissEhrRecentMedicationRequests: (state, { payload }: PayloadAction<string>) => {
      if (state.launchInstanceId !== payload || state.phase !== 'ready') return
      state.phase = 'dismissed'
      state.result = undefined
      state.represcribing = undefined
    },
    ehrReprescribingStarted: (
      state,
      { payload }: PayloadAction<{ launchInstanceId: string; rowIndex: number; attemptId: string }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.phase !== 'ready' ||
        state.represcribing?.phase === 'matching' ||
        state.result?.status !== 'available' ||
        state.result.medications[payload.rowIndex]?.represcribing.eligibility !== 'eligible'
      )
        return
      state.represcribing = {
        phase: 'matching',
        rowIndex: payload.rowIndex,
        attemptId: payload.attemptId,
      }
    },
    ehrReprescribingFailed: (
      state,
      { payload }: PayloadAction<{ launchInstanceId: string; attemptId: string; message: string }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.represcribing?.phase !== 'matching' ||
        state.represcribing.attemptId !== payload.attemptId
      )
        return
      state.represcribing.phase = 'error'
      state.represcribing.message = payload.message
    },
    ehrReprescribingSearchOpened: (
      state,
      {
        payload,
      }: PayloadAction<{
        launchInstanceId: string
        attemptId: string
        slotIndex: number
        prefill: EhrReprescribingPrefill
      }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.represcribing?.phase !== 'matching' ||
        state.represcribing.attemptId !== payload.attemptId
      )
        return
      state.phase = 'dismissed'
      state.result = undefined
      state.represcribing = undefined
      state.pendingSeed = {
        launchInstanceId: payload.launchInstanceId,
        slotIndex: payload.slotIndex,
        prefill: payload.prefill,
      }
    },
    ehrReprescribingCommitted: (
      state,
      { payload }: PayloadAction<{ launchInstanceId: string; attemptId: string }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.represcribing?.phase !== 'matching' ||
        state.represcribing.attemptId !== payload.attemptId
      )
        return
      state.phase = 'dismissed'
      state.result = undefined
      state.represcribing = undefined
      state.pendingSeed = undefined
    },
    beginEhrProductSelection: (
      state,
      { payload }: PayloadAction<{ slotIndex: number; selectionAttemptId: string }>
    ) => {
      if (
        state.pendingSeed?.slotIndex !== payload.slotIndex ||
        state.pendingSeed.selectionAttemptId
      )
        return
      state.pendingSeed.selectionAttemptId = payload.selectionAttemptId
    },
    failEhrProductSelection: (
      state,
      { payload }: PayloadAction<{ slotIndex: number; selectionAttemptId: string }>
    ) => {
      if (
        state.pendingSeed?.slotIndex !== payload.slotIndex ||
        state.pendingSeed.selectionAttemptId !== payload.selectionAttemptId
      )
        return
      state.pendingSeed.selectionAttemptId = undefined
    },
    completeEhrProductSelection: (
      state,
      { payload }: PayloadAction<{ slotIndex: number; selectionAttemptId?: string }>
    ) => {
      if (
        state.pendingSeed?.slotIndex !== payload.slotIndex ||
        (payload.selectionAttemptId &&
          state.pendingSeed.selectionAttemptId !== payload.selectionAttemptId)
      )
        return
      state.pendingSeed = undefined
    },
    cancelEhrReprescribingSeed: (state) => {
      state.pendingSeed = undefined
      if (state.represcribing) {
        state.phase = 'dismissed'
        state.result = undefined
        state.represcribing = undefined
      }
    },
    resetEhrRecentMedicationRequests: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(ehrLaunchEstablished, (state, { payload }) => {
        if (!payload.reset && state.launchInstanceId === payload.launchInstanceId) return
        return { phase: 'idle', launchInstanceId: payload.launchInstanceId }
      })
      .addCase(ehrPatientInvalidated, () => initialState)
      .addCase(ehrPatientAcquisitionCleared, () => initialState)
      .addCase(resetScript, (state) => {
        state.pendingSeed = undefined
        state.represcribing = undefined
      })
  },
})

export const {
  beginEhrProductSelection,
  cancelEhrReprescribingSeed,
  completeEhrProductSelection,
  dismissEhrRecentMedicationRequests,
  ehrRecentMedicationRequestsReady,
  ehrRecentMedicationRequestsStarted,
  ehrReprescribingCommitted,
  ehrReprescribingFailed,
  ehrReprescribingSearchOpened,
  ehrReprescribingStarted,
  failEhrProductSelection,
  resetEhrRecentMedicationRequests,
} = slice.actions

export default slice.reducer
